import { useMemo } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { LoomAttributionLogo } from '@atlaskit/logo';
import { token } from '@atlaskit/tokens';

import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { LogoV3 } from '@/libraries/wpl/foundations/src/logo-v3';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';
import { MotionContainer } from '@/patterns/global-nav/src/components/motion-container';
import type { DropdownType } from '@/patterns/global-nav/src/mobile-nav/mobile-nav';
import type { MobileNavLinkContentProps } from '@/patterns/global-nav/src/mobile-nav/mobile-nav-links-container';
import {
	DivOnHoverExit,
	type GlobalNavLinkContentProps,
} from '@/patterns/global-nav/src/nav-links-container';
import { ProductPageSubNav } from '@/patterns/global-nav/src/nav-product-page';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { LoomFeaturesSubnav } from './subnavs/features-subnav';
import { LoomSolutionsSubnav } from './subnavs/solutions-subnav';

const wrapSubnav = (subnav: JSX.Element) => {
	return (
		<DivOnHoverExit css={css({ position: 'relative' })}>
			<TypographyStyles>
				<MotionContainer direction="none">
					<div
						css={css({
							paddingBlock: token('space.500'),
							paddingInline: token('space.500'),
						})}
					>
						{subnav}
					</div>
				</MotionContainer>
			</TypographyStyles>
		</DivOnHoverExit>
	);
};

const LoomLogoComponent = () => {
	const gwp7518NavIconUpdates = checkFeatureFlagGate(featureFlags.gwp7518NavIconUpdates);

	return gwp7518NavIconUpdates ? (
		<LogoV3 brand="loomcircular" type="logo" appearance="brand" />
	) : (
		<LoomAttributionLogo appearance="brand" />
	);
};

export const LoomNav = () => {
	const intl = useIntl();

	const dropdownStyles = useMemo(() => {
		return {
			width: 'fit-content !important',
			margin: 0,
			insetBlockStart: '4.25rem',
			transform: 'translateX(-25%)',
		};
	}, []);

	const signupLink = useLocalizedHref('/try/cloud/signup?bundle=loom&edition=premium');
	const resourcesHref = useLocalizedHref('/software/loom/resources');
	const pricingHref = useLocalizedHref('/software/loom/pricing');

	const desktopLinks: GlobalNavLinkContentProps[] = [
		{
			globalLinksKey: 'features',
			productKey: 'loom',
			linkText: intl.formatMessage({
				id: 'LoomPageTemplate.navbar.features',
				defaultMessage: 'Features',
			}),
			hasSubnav: true,
			content: wrapSubnav(<LoomFeaturesSubnav />),
			containerPosition: 'relative',
		},
		{
			globalLinksKey: 'solutions',
			productKey: 'loom',
			linkText: intl.formatMessage({
				id: 'LoomPageTemplate.navbar.resources',
				defaultMessage: 'Solutions',
			}),
			hasSubnav: true,
			containerStyles: dropdownStyles,
			content: wrapSubnav(<LoomSolutionsSubnav />),
			containerPosition: 'relative',
		},
		{
			globalLinksKey: 'resources',
			productKey: 'loom',
			linkText: intl.formatMessage({
				id: 'LoomPageTemplate.navbar.useCases',
				defaultMessage: 'Resources',
			}),
			hasSubnav: false,
			href: resourcesHref,
		},
		{
			globalLinksKey: 'pricing',
			productKey: 'loom',
			linkText: intl.formatMessage({
				id: 'LoomPageTemplate.navbar.pricing',
				defaultMessage: 'Pricing',
			}),
			hasSubnav: false,
			href: pricingHref,
		},
	];

	const mobileLinks: MobileNavLinkContentProps[] = [
		...desktopLinks.map(
			(link): MobileNavLinkContentProps => ({
				globalLinksKey: link.globalLinksKey,
				productKey: link.productKey,
				linkText: link.linkText,
				hasSubnav: link.hasSubnav,
				href: link.href,
				dropdownType: link.globalLinksKey as DropdownType,
			}),
		),
	];

	return (
		<ProductPageSubNav
			productKey={'loom'}
			productLogoUrl="/software/loom"
			productLogo={<LoomLogoComponent />}
			productLogoAriaLabel={intl.formatMessage({
				id: 'LoomPageTemplate.navbar.loom-logo',
				defaultMessage: 'Loom logo',
			})}
			mobileCtaText={intl.formatMessage({
				id: 'LoomPageTemplate.navbar.getitfree',
				defaultMessage: 'Get it Free',
			})}
			navigationTrynowCtaText={intl.formatMessage({
				id: 'LoomPageTemplate.navbar.getitfree',
				defaultMessage: 'Get it Free',
			})}
			mobileCtaUrl={signupLink}
			mobileLogo={<LoomLogoComponent />}
			productPageNavLinks={desktopLinks}
			navigationTrynowCtaUrl={signupLink}
			mobileLinksSections={[
				{
					mobileLinkKey: 'mobile-loom',
					mobileLinksProps: mobileLinks,
				},
			]}
			additionalMenuPageObjectsArray={[
				{
					menuPageKey: 'features',
					menuPageObjects: <LoomFeaturesSubnav />,
				},
				{
					menuPageKey: 'solutions',
					menuPageObjects: <LoomSolutionsSubnav />,
				},
			]}
		/>
	);
};

export default LoomNav;
